/**
 * Koji CZ — přidání upsell produktu z cart draweru (koji-cart-upsell.liquid).
 *
 * Klik na „Přidat" přidá 1 ks vybrané varianty jako samostatnou položku BEZ vlastnosti
 * `_bundle` (aby nespadla do tieru bundle discount function), přes Horizon event
 * `CartLinesUpdateEvent` nechá cart drawer refreshnout (položka se objeví, přepočítá se
 * doprava zdarma, upsell se sám skryje). Delegace na document → přežije re-render sekce.
 *
 * Fallback: při chybě přesměruje na /cart.
 */

import { CartLinesUpdateEvent } from '@shopify/events';

function routeBase() {
  const r =
    window.Shopify && Shopify.routes && Shopify.routes.root
      ? Shopify.routes.root
      : '/';
  return r.replace(/\/$/, '');
}

async function addUpsell(btn) {
  const id = Number(btn.getAttribute('data-variant-id'));
  if (!id) return;

  const base = routeBase();
  const discountCode = btn.getAttribute('data-discount-code');
  const expectedPrice = Number(btn.getAttribute('data-upsell-price'));
  const expectedCurrency = btn.getAttribute('data-upsell-currency');
  const items = [{ id, quantity: 1 }];
  if (discountCode) items[0].properties = { _koji_upsell: discountCode };
  let previousCodes = [];
  let added = false;

  btn.setAttribute('disabled', 'disabled');
  btn.classList.add('is-loading');

  const sectionIds = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el.dataset && el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
  });
  const sections = Array.from(sectionIds).join(',');
  const deferred = CartLinesUpdateEvent.createPromise();

  try {
    if (discountCode) {
      const beforeResponse = await fetch(base + '/cart.js', {
        headers: { Accept: 'application/json' },
      });
      if (!beforeResponse.ok) throw new Error('cart unavailable');
      const before = await beforeResponse.json();
      previousCodes = (before.discount_codes || []).map((entry) => entry.code).filter(Boolean);
      if (before.currency !== expectedCurrency || !Number.isFinite(expectedPrice) || expectedPrice < 0) {
        throw new Error('upsell price unavailable');
      }
    }

    btn.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((it) => ({
          merchandiseId: 'gid://shopify/ProductVariant/' + it.id,
          quantity: it.quantity,
        })),
        promise: deferred.promise,
      })
    );

    const res = await fetch(base + '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items,
        sections,
        sections_url: window.location.pathname,
      }),
    });
    if (!res.ok) throw new Error('add failed');
    const data = await res.json();
    added = true;

    let ajaxCart = null;
    let renderedSections = data.sections;
    if (discountCode) {
      const codes = [...previousCodes];
      if (!codes.some((code) => code.toUpperCase() === discountCode.toUpperCase())) codes.push(discountCode);
      const discountResponse = await fetch(base + '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          discount: codes.join(','),
          sections,
          sections_url: window.location.pathname,
        }),
      });
      if (!discountResponse.ok) throw new Error('upsell discount unavailable');
      ajaxCart = await discountResponse.json();
      const line = ajaxCart.items.find(
        (item) => item.variant_id === id && item.properties?._koji_upsell === discountCode
      );
      const accepted = (ajaxCart.discount_codes || []).some(
        (entry) => entry.code.toUpperCase() === discountCode.toUpperCase() && entry.applicable
      );
      if (!accepted || !line || line.final_price > expectedPrice || ajaxCart.currency !== expectedCurrency) {
        throw new Error('upsell discount unavailable');
      }
      renderedSections = ajaxCart.sections;
    }

    let cart = null;
    try {
      if (!ajaxCart) {
        const cres = await fetch(base + '/cart.js', {
          headers: { Accept: 'application/json' },
        });
        if (!cres.ok) throw new Error('cart unavailable');
        ajaxCart = await cres.json();
      }
      cart = CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart);
    } catch (e) {
    }

    deferred.resolve({
      cart,
      detail: {
        sections: renderedSections,
        items: data.items,
        source: 'koji-upsell',
        itemCount: 1,
      },
    });
  } catch (e) {
    if (discountCode && added) {
      try {
        const currentResponse = await fetch(base + '/cart.js', {
          headers: { Accept: 'application/json' },
        });
        if (!currentResponse.ok) throw new Error('cart unavailable');
        const current = await currentResponse.json();
        const line = current.items.find(
          (item) => item.variant_id === id && item.properties?._koji_upsell === discountCode
        );
        if (line) {
          const rollback = await fetch(base + '/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: Math.max(0, line.quantity - 1) }),
          });
          if (!rollback.ok) throw new Error('cart unavailable');
        }
        await fetch(base + '/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: previousCodes.join(',') }),
        });
      } catch (rollbackError) {
      }
    }
    if (typeof deferred.reject === 'function') deferred.reject(e);
    window.location.href = base + '/cart';
    return;
  } finally {
    btn.removeAttribute('disabled');
    btn.classList.remove('is-loading');
  }
}

// Karuzela upselli — CROSSFADE: przełączamy klasę .is-active na slajdach (opacity),
// slajdy ułożone w grid-stacku (CSS). Bez translateX/scroll (scrollLeft zablokowany w <dialog>).
function navCarousel(navBtn) {
  const carousel = navBtn.closest('[data-koji-upsell-carousel]');
  if (!carousel) return;
  const slides = carousel.querySelectorAll('.koji-upsell__slide');
  const count = slides.length;
  if (count < 2) return;
  const dir = navBtn.getAttribute('data-koji-upsell-nav') === 'next' ? 1 : -1;
  let idx = parseInt(carousel.getAttribute('data-index') || '0', 10) + dir;
  if (idx < 0) idx = count - 1;
  if (idx > count - 1) idx = 0;
  carousel.setAttribute('data-index', String(idx));
  slides.forEach((slide, i) => {
    const active = i === idx;
    slide.classList.toggle('is-active', active);
    slide.setAttribute('aria-hidden', active ? 'false' : 'true');
  });
}

// Delegace na document — funguje i po re-renderu section-rendering API.
if (!window.__kojiUpsellInit) {
  window.__kojiUpsellInit = true;
  document.addEventListener('click', (e) => {
    if (!e.target.closest) return;
    const addBtn = e.target.closest('[data-koji-upsell-add]');
    if (addBtn) {
      addUpsell(addBtn);
      return;
    }
    const navBtn = e.target.closest('[data-koji-upsell-nav]');
    if (navBtn) navCarousel(navBtn);
  });
}
